import React, { useEffect, useMemo, useRef, useState } from "react";
import { Prompt, useHistory, useParams } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  Col,
  Collapse,
  DatePicker,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Skeleton,
  Space,
  Statistic,
  Tooltip,
  Typography
} from "antd";
import {
  ArrowLeftOutlined,
  CameraOutlined,
  CopyOutlined,
  ExclamationCircleOutlined,
  PlusOutlined,
  SaveOutlined,
  ScanOutlined
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { generalErrorAlert } from "../../../commons/GeneralErrorAlert";
import { Success, Error as ErrorMessage } from "../../../commons/Messages/Messages";
import ResponsiveTable from "../../../design-system/ResponsiveTable";
import {
  createMeasurement,
  fetchProjects,
  getMeasurement,
  getMeta,
  updateMeasurement
} from "./api";
import { normalizeRoom, summarizeMeasurement } from "./geometry";
import { formatM, formatM2, formatM3 } from "./format";
import DictationBox from "./DictationBox";
import RoomCard from "./RoomCard";
import PhotoMeasure from "./PhotoMeasure";
import ArMeasure, { useArSupported } from "./ArMeasure";
import OfflineNotice from "../../../offline/OfflineNotice";
import { useSyncedRecord } from "../../../offline/hooks";
import "./measurements.css";

const RULE_OPTIONS = [
  { value: "painter", label: "Painting, plaster, drywall (VOB: deduct openings over 2.5 m²)" },
  { value: "tiler", label: "Tiles, floor coverings (VOB: deduct over 0.1 m²)" },
  { value: "exact", label: "Exact (deduct everything)" },
  { value: "custom", label: "Own limits" }
];
const PRESETS = {
  painter: { wallDeductOver: 2.5, floorDeductOver: 2.5 },
  tiler: { wallDeductOver: 0.1, floorDeductOver: 0.1 },
  exact: { wallDeductOver: 0, floorDeductOver: 0 }
};
const DRAFT_PREFIX = "measurement-draft:";

let keySeed = 0;
const newKey = () => `r${Date.now().toString(36)}${(keySeed += 1)}`;
const withKey = room => ({ ...normalizeRoom(room), key: room.key || room._id || newKey() });

const readDraft = id => {
  try {
    return JSON.parse(localStorage.getItem(DRAFT_PREFIX + id) || "null");
  } catch {
    return null;
  }
};
const writeDraft = (id, draft) => {
  try {
    if (draft) localStorage.setItem(DRAFT_PREFIX + id, JSON.stringify(draft));
    else localStorage.removeItem(DRAFT_PREFIX + id);
  } catch {
    // Storage full or blocked: the draft is only a safety net.
  }
};

const hasDimensions = room =>
  room.shape === "rectangle"
    ? Boolean(room.length || room.width)
    : room.shape === "quad"
      ? room.sides.some(Boolean)
      : room.walls.some(wall => wall.length);

/** Dictated rooms complete rooms with the same name; the others are added. */
const mergeRooms = (current, found) => {
  const result = [...current];
  found.forEach(room => {
    const name = room.name.trim().toLowerCase();
    const index = name ? result.findIndex(r => r.name.trim().toLowerCase() === name) : -1;
    if (index < 0) {
      result.push(withKey(room));
      return;
    }
    const existing = result[index];
    const merged = { ...existing };
    if (hasDimensions(room)) {
      Object.assign(merged, {
        shape: room.shape,
        length: room.length,
        width: room.width,
        sides: room.sides,
        diagonal: room.diagonal,
        diagonal2: room.diagonal2,
        walls: room.walls
      });
    }
    if (room.height) merged.height = room.height;
    if (room.level) merged.level = room.level;
    merged.openings = [...existing.openings, ...room.openings];
    merged.adjustments = [...existing.adjustments, ...room.adjustments];
    if (room.notes) merged.notes = [existing.notes, room.notes].filter(Boolean).join("\n");
    result[index] = merged;
  });
  return result;
};

const emptyForm = t => ({
  title: `${t("Measurement")} ${dayjs().format("DD.MM.YYYY")}`,
  date: dayjs().format("YYYY-MM-DD"),
  project: null,
  address: "",
  notes: "",
  dictation: "",
  rules: { preset: "painter", ...PRESETS.painter }
});

const MeasurementEditor = () => {
  const { t, i18n } = useTranslation("dashboard");
  const lng = i18n.language;
  const { id } = useParams();
  const history = useHistory();
  const isNew = id === "new";
  const [meta, setMeta] = useState(null);
  const [form, setForm] = useState(() => emptyForm(t));
  const [rooms, setRooms] = useState([]);
  const [openRooms, setOpenRooms] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [draft, setDraft] = useState(null);
  const [tool, setTool] = useState(null);
  const [photoOpen, setPhotoOpen] = useState(false);
  const [chooserOpen, setChooserOpen] = useState(false);
  const arSupported = useArSupported();
  const ar = useRef(null);
  // Set when "new" was just saved: the redirect to its id must not load it again (that
  // would flash a skeleton and close the open rooms).
  const justCreated = useRef(null);
  const draftId = isNew ? "new" : id;
  // Created offline: moves to the real address once uploaded
  useSyncedRecord(id, serverId => history.replace(`/user/measurements/${serverId}`), {
    when: !dirty
  });

  const canEdit = meta ? (isNew ? meta.can.create : meta.can.update) : false;
  const readOnly = !canEdit;

  useEffect(() => {
    getMeta()
      .then(res => setMeta(res.data))
      .catch(generalErrorAlert);
    fetchProjects()
      .then(setProjects)
      .catch(() => {});
  }, []);

  const loadFrom = data => {
    setForm({
      title: data.title,
      date: data.date || "",
      project: data.project?._id || data.project || null,
      address: data.address || "",
      notes: data.notes || "",
      dictation: data.dictation || "",
      rules: data.rules || { preset: "painter", ...PRESETS.painter }
    });
    if (data.project?._id) {
      setProjects(prev =>
        prev.some(p => p._id === data.project._id) ? prev : [...prev, data.project]
      );
    }
    setRooms((data.rooms || []).map(withKey));
  };

  useEffect(() => {
    if (isNew) {
      const saved = readDraft("new");
      if (saved) setDraft(saved);
      return;
    }
    if (justCreated.current === id) {
      justCreated.current = null;
      return;
    }
    setLoading(true);
    getMeasurement(id)
      .then(res => {
        loadFrom(res.data);
        setOpenRooms(res.data.rooms?.length === 1 ? [String(res.data.rooms[0]._id)] : []);
        const saved = readDraft(id);
        if (saved && new Date(saved.savedAt) > new Date(res.data.updatedAt)) setDraft(saved);
      })
      .catch(err => {
        generalErrorAlert(err);
        history.replace("/user/measurements");
      })
      .finally(() => setLoading(false));
  }, [id]);

  // Keep unsaved work on the device: sites often have no reception.
  useEffect(() => {
    if (!dirty) return undefined;
    const timer = setTimeout(
      () => writeDraft(draftId, { savedAt: new Date().toISOString(), form, rooms }),
      800
    );
    return () => clearTimeout(timer);
  }, [dirty, form, rooms, draftId]);

  const summary = useMemo(() => summarizeMeasurement(rooms, form.rules), [rooms, form.rules]);

  const updateForm = patch => {
    setForm(prev => ({ ...prev, ...patch }));
    setDirty(true);
  };
  const updateRooms = updater => {
    setRooms(updater);
    setDirty(true);
  };

  const addRoom = () => {
    const last = rooms[rooms.length - 1];
    const room = withKey({
      name: `${t("Room")} ${rooms.length + 1}`,
      level: last?.level || "",
      height: last?.height || 0
    });
    updateRooms(prev => [...prev, room]);
    setOpenRooms(prev => [...prev, room.key]);
  };

  const duplicateRoom = index => {
    const copy = withKey({ ...rooms[index], _id: undefined, key: undefined });
    copy.name = `${rooms[index].name} (2)`;
    updateRooms(prev => [...prev.slice(0, index + 1), copy, ...prev.slice(index + 1)]);
    setOpenRooms(prev => [...prev, copy.key]);
  };

  const applyDictation = found => {
    const before = rooms.length;
    const merged = mergeRooms(rooms, found);
    updateRooms(() => merged);
    setOpenRooms(prev => [...prev, ...merged.slice(before).map(room => room.key)]);
    Success(t("Rooms updated. Please check the values."));
  };

  // The camera button of a field. Where live AR is possible, the user picks the method.
  const openMeasure = (label, apply) => {
    const next = { label, apply };
    setTool(next);
    if (arSupported) setChooserOpen(true);
    else setPhotoOpen(true);
  };

  const save = async () => {
    if (!form.title.trim()) {
      ErrorMessage(t("Please give the measurement a name."));
      return;
    }
    setSaving(true);
    const body = {
      ...form,
      rooms: rooms.map(room => {
        const saved = { ...room };
        delete saved.key; // client-only
        return saved;
      })
    };
    try {
      const res = isNew ? await createMeasurement(body) : await updateMeasurement(id, body);
      writeDraft(draftId, null);
      setDirty(false);
      setDraft(null);
      // Keep the open panels: saved rooms come back in the same order with ids.
      const keys = rooms.map(room => room.key);
      setRooms(res.data.rooms.map((room, i) => ({ ...withKey(room), key: keys[i] || room._id })));
      Success(t("Measurement saved"));
      // After the render that clears `dirty`, or the leave prompt would stop the redirect.
      if (isNew) {
        justCreated.current = res.data._id;
        setTimeout(() => history.replace(`/user/measurements/${res.data._id}`), 0);
      }
    } catch (err) {
      generalErrorAlert(err);
      if (!err.response) {
        ErrorMessage(t("No connection. Your changes are kept on this device."));
      }
    } finally {
      setSaving(false);
    }
  };

  const restoreDraft = () => {
    setForm(draft.form);
    setRooms(draft.rooms.map(withKey));
    setDirty(true);
    setDraft(null);
  };

  const discardDraft = () => {
    writeDraft(draftId, null);
    setDraft(null);
  };

  const copyTable = async () => {
    const number = value =>
      new Intl.NumberFormat(lng === "en" ? "en-GB" : "de-DE", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
        useGrouping: false
      }).format(value);
    const header = [
      t("Room"),
      t("Floor level"),
      `${t("Floor")} m²`,
      `${t("Walls (net)")} m²`,
      `${t("Ceiling")} m²`,
      `${t("Perimeter")} m`,
      `${t("Skirting")} m`,
      `${t("Volume")} m³`
    ];
    const lines = rooms.map((room, i) => {
      const f = summary.rooms[i].figures;
      return [
        room.name,
        room.level,
        number(f.floorNet),
        number(f.wallNet),
        number(f.ceilingNet),
        number(f.perimeter),
        number(f.skirting),
        number(f.volume)
      ];
    });
    const s = summary.totals;
    lines.push([
      t("Total"),
      "",
      number(s.floorNet),
      number(s.wallNet),
      number(s.ceilingNet),
      number(s.perimeter),
      number(s.skirting),
      number(s.volume)
    ]);
    try {
      await navigator.clipboard.writeText([header, ...lines].map(row => row.join("\t")).join("\n"));
      Success(t("Copied. Paste it into a spreadsheet or an offer."));
    } catch {
      ErrorMessage(t("Copying is not allowed here."));
    }
  };

  if (loading) {
    return (
      <div className="measurements-page">
        <Card>
          <Skeleton active />
        </Card>
      </div>
    );
  }

  const rulePreset = form.rules.preset;
  const roomItems = rooms.map((room, index) => {
    const roomSummary = summary.rooms[index];
    const warn = roomSummary.warnings.some(
      w => !["NO_HEIGHT", "MISSING_DIMENSIONS"].includes(w.code)
    );
    return {
      key: room.key,
      label: (
        <div className="ms-room-head">
          <Typography.Text strong>{room.name || `${t("Room")} ${index + 1}`}</Typography.Text>
          {room.level && <Typography.Text type="secondary"> · {room.level}</Typography.Text>}
          {warn && (
            <Tooltip title={t("Please check this room")}>
              <ExclamationCircleOutlined className="ms-warn-icon" />
            </Tooltip>
          )}
        </div>
      ),
      extra: roomSummary.valid ? (
        <span className="ms-room-extra">
          {formatM2(roomSummary.figures.floorNet, lng)}
          {room.height > 0 && ` · ${formatM2(roomSummary.figures.wallNet, lng)}`}
        </span>
      ) : null,
      children: (
        <RoomCard
          room={room}
          summary={roomSummary}
          readOnly={readOnly}
          onChange={next => updateRooms(prev => prev.map((r, i) => (i === index ? next : r)))}
          onRemove={() => updateRooms(prev => prev.filter((_, i) => i !== index))}
          onDuplicate={() => duplicateRoom(index)}
          onMeasure={openMeasure}
        />
      )
    };
  });

  const totalColumns = [
    { title: t("Room"), dataIndex: "name", key: "name", mobile: "title" },
    {
      title: t("Floor"),
      key: "floor",
      align: "right",
      render: (_, row) => formatM2(row.f.floorNet, lng)
    },
    {
      title: t("Walls (net)"),
      key: "walls",
      align: "right",
      render: (_, row) => formatM2(row.f.wallNet, lng)
    },
    {
      title: t("Ceiling"),
      key: "ceiling",
      align: "right",
      render: (_, row) => formatM2(row.f.ceilingNet, lng)
    },
    {
      title: t("Perimeter"),
      key: "perimeter",
      align: "right",
      render: (_, row) => formatM(row.f.perimeter, lng)
    },
    {
      title: t("Skirting"),
      key: "skirting",
      align: "right",
      render: (_, row) => formatM(row.f.skirting, lng)
    },
    {
      title: t("Volume"),
      key: "volume",
      align: "right",
      render: (_, row) => formatM3(row.f.volume, lng)
    }
  ];
  const totalRows = rooms.map((room, i) => ({
    key: room.key,
    name: [room.name || `${t("Room")} ${i + 1}`, room.level].filter(Boolean).join(" · "),
    f: summary.rooms[i].figures
  }));

  return (
    <div className="measurements-page">
      <Prompt when={dirty && !saving} message={t("You have unsaved changes. Leave anyway?")} />
      {!isNew && <OfflineNotice module="measurements" record={{ _id: id }} />}

      {draft && (
        <Alert
          type="info"
          showIcon
          className="mb-3"
          message={t("Unsaved changes from {{time}} were found on this device.", {
            time: dayjs(draft.savedAt).format("DD.MM.YYYY HH:mm")
          })}
          action={
            <Space direction="vertical" size={4}>
              <Button size="small" type="primary" onClick={restoreDraft}>
                {t("Restore")}
              </Button>
              <Button size="small" onClick={discardDraft}>
                {t("Discard")}
              </Button>
            </Space>
          }
        />
      )}

      <Card className="mb-3">
        <div className="ms-header">
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            aria-label={t("Back")}
            onClick={() => history.push("/user/measurements")}
          />
          <Input
            size="large"
            className="ms-title-input"
            value={form.title}
            readOnly={readOnly}
            placeholder={t("Name of the measurement")}
            onChange={event => updateForm({ title: event.target.value })}
          />
          {!readOnly && (
            <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={save}>
              {t("Save")}
            </Button>
          )}
        </div>
        <Form layout="vertical" className="ms-meta-form">
          <Row gutter={[12, 0]}>
            <Col xs={12} md={6}>
              <Form.Item label={t("Date")}>
                <DatePicker
                  className="ms-full"
                  format="DD.MM.YYYY"
                  value={form.date ? dayjs(form.date) : null}
                  disabled={readOnly}
                  onChange={value => updateForm({ date: value ? value.format("YYYY-MM-DD") : "" })}
                />
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item label={t("Project")}>
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  value={form.project || undefined}
                  disabled={readOnly}
                  placeholder={t("None")}
                  onChange={value => updateForm({ project: value || null })}
                  options={projects.map(p => ({ value: p._id, label: p.projectName }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label={t("Address")}>
                <Input
                  value={form.address}
                  readOnly={readOnly}
                  onChange={event => updateForm({ address: event.target.value })}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={rulePreset === "custom" ? 12 : 24}>
              <Form.Item
                label={t("Deductions")}
                extra={t("Decides which openings and cut-outs are deducted from the areas.")}
              >
                <Select
                  value={rulePreset}
                  disabled={readOnly}
                  onChange={preset =>
                    updateForm({
                      rules: { preset, ...(PRESETS[preset] || form.rules) }
                    })
                  }
                  options={RULE_OPTIONS.map(option => ({ ...option, label: t(option.label) }))}
                />
              </Form.Item>
            </Col>
            {rulePreset === "custom" && (
              <>
                <Col xs={12} md={6}>
                  <Form.Item label={t("Walls/ceiling: deduct over")}>
                    <InputNumber
                      className="ms-full"
                      min={0}
                      step={0.1}
                      addonAfter="m²"
                      value={form.rules.wallDeductOver}
                      disabled={readOnly}
                      onChange={value =>
                        updateForm({ rules: { ...form.rules, wallDeductOver: value ?? 0 } })
                      }
                    />
                  </Form.Item>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Item label={t("Floor: deduct over")}>
                    <InputNumber
                      className="ms-full"
                      min={0}
                      step={0.1}
                      addonAfter="m²"
                      value={form.rules.floorDeductOver}
                      disabled={readOnly}
                      onChange={value =>
                        updateForm({ rules: { ...form.rules, floorDeductOver: value ?? 0 } })
                      }
                    />
                  </Form.Item>
                </Col>
              </>
            )}
          </Row>
        </Form>
      </Card>

      <DictationBox
        value={form.dictation}
        readOnly={readOnly}
        voiceConfigured={Boolean(meta?.voiceConfigured)}
        voiceTranslateTo={meta?.voiceTranslateTo}
        rules={form.rules}
        onChange={next => {
          setForm(prev => ({
            ...prev,
            dictation: typeof next === "function" ? next(prev.dictation) : next
          }));
          setDirty(true);
        }}
        onApply={applyDictation}
      />

      <Card
        className="mb-3 ms-rooms"
        title={`${t("Rooms")} (${rooms.length})`}
        extra={
          <Space wrap>
            <Tooltip title={t("Measure distances on a photo")}>
              <Button icon={<CameraOutlined />} onClick={() => openMeasure(null, null)}>
                {t("Camera")}
              </Button>
            </Tooltip>
            {!readOnly && (
              <Button type="primary" icon={<PlusOutlined />} onClick={addRoom}>
                {t("Add room")}
              </Button>
            )}
          </Space>
        }
      >
        {rooms.length ? (
          <Collapse
            activeKey={openRooms}
            onChange={keys => setOpenRooms(Array.isArray(keys) ? keys : [keys])}
            items={roomItems}
          />
        ) : (
          <Empty
            description={t(
              "No rooms yet. Add a room and enter its dimensions, or dictate them above."
            )}
          />
        )}
      </Card>

      {rooms.length > 0 && (
        <Card
          className="mb-3"
          title={t("Summary")}
          extra={
            <Button icon={<CopyOutlined />} onClick={copyTable}>
              {t("Copy as table")}
            </Button>
          }
        >
          <Row gutter={[16, 16]} className="mb-3">
            <Col xs={12} md={6}>
              <Statistic title={t("Floor")} value={formatM2(summary.totals.floorNet, lng)} />
            </Col>
            <Col xs={12} md={6}>
              <Statistic title={t("Walls (net)")} value={formatM2(summary.totals.wallNet, lng)} />
            </Col>
            <Col xs={12} md={6}>
              <Statistic title={t("Ceiling")} value={formatM2(summary.totals.ceilingNet, lng)} />
            </Col>
            <Col xs={12} md={6}>
              <Statistic title={t("Skirting")} value={formatM(summary.totals.skirting, lng)} />
            </Col>
          </Row>
          <ResponsiveTable
            rowKey="key"
            size="small"
            columns={totalColumns}
            dataSource={totalRows}
            pagination={false}
            scroll={{ x: 720 }}
            mobileAvatar={false}
          />
        </Card>
      )}

      {!readOnly && rooms.length > 0 && (
        <div className="ms-bottom-save">
          <Button
            type="primary"
            size="large"
            block
            icon={<SaveOutlined />}
            loading={saving}
            onClick={save}
          >
            {t("Save")}
          </Button>
        </div>
      )}

      <PhotoMeasure
        open={photoOpen}
        target={tool?.apply ? tool : null}
        onClose={() => setPhotoOpen(false)}
      />
      <ArMeasure ref={ar} onUnsupported={() => setPhotoOpen(true)} />
      <Modal
        open={chooserOpen}
        title={t("Measure with the camera")}
        footer={null}
        onCancel={() => setChooserOpen(false)}
      >
        <Space direction="vertical" className="ms-full">
          <Button
            block
            size="large"
            type="primary"
            icon={<ScanOutlined />}
            onClick={() => {
              setChooserOpen(false);
              ar.current?.start(tool?.apply ? tool : null);
            }}
          >
            {t("Live (AR)")}
          </Button>
          <Typography.Text type="secondary">
            {t("Quick, about ±1 cm per metre. Point at the start and end.")}
          </Typography.Text>
          <Button
            block
            size="large"
            icon={<CameraOutlined />}
            onClick={() => {
              setChooserOpen(false);
              setPhotoOpen(true);
            }}
          >
            {t("From a photo")}
          </Button>
          <Typography.Text type="secondary">
            {t("With a reference of known size. Shows how exact each value is.")}
          </Typography.Text>
        </Space>
      </Modal>
    </div>
  );
};

export default MeasurementEditor;
